import {
  CannedResponse,
  ConversationsSnapshot,
  EmailAddress,
  EmailChain,
  ThreadEmail,
  ThreadMailbox,
} from "../types";

/**
 * Seed data for the mock conversation service. Delete this file once the
 * Helpdesk conversation APIs are live. Times are relative to `now` so the list
 * always reads as recent activity.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const MAIL_DOMAIN = "dickersontransportation.com";

/** Same record as `mbx-customercare` in the Mailboxes mock. */
export const CUSTOMER_CARE_MAILBOX: ThreadMailbox = {
  id: "mbx-customercare",
  address: `customercare@${MAIL_DOMAIN}`,
  senderName: "Dickerson Customer Care",
};

const mailboxAddress: EmailAddress = {
  name: CUSTOMER_CARE_MAILBOX.senderName,
  address: CUSTOMER_CARE_MAILBOX.address,
};

const priya: EmailAddress = { name: "Priya Raman", address: "priya@fornoliving.com" };
const fornoOps: EmailAddress = { name: "Forno Living Ops", address: "ops@fornoliving.com" };
const ana: EmailAddress = { name: "Ana Reyes", address: "dispatch@sdtransfer.com" };

const ORDER = {
  orderNumber: "DTSODR009352-2",
  customerName: "Forno Living",
  serviceLevel: "2 Man Threshold",
};

const CHAINS: Record<"customer" | "agent", EmailChain> = {
  customer: { party: "customer", organisation: "Forno Living", participants: [priya, fornoOps] },
  agent: { party: "agent", organisation: "S&D Transfer Logistics", participants: [ana] },
};

const messageId = (key: string) => `<${key}@${MAIL_DOMAIN}>`;

/** An email from outside (customer or agent) into the mailbox. */
const inbound = (
  email: Omit<ThreadEmail, "direction" | "attachments" | "messageId"> &
    Partial<Pick<ThreadEmail, "attachments">>
): ThreadEmail => ({
  ...email,
  messageId: messageId(email.id),
  direction: "inbound",
  attachments: email.attachments ?? [],
});

/** A reply a team member sent from the mailbox. */
const outbound = (
  email: Omit<ThreadEmail, "direction" | "attachments" | "messageId" | "from">
): ThreadEmail => ({
  ...email,
  messageId: messageId(email.id),
  direction: "outbound",
  from: mailboxAddress,
  attachments: [],
  deliveryStatus: "delivered",
});

export const buildMockSnapshot = (now: number): ConversationsSnapshot => {
  const at = (msAgo: number) => new Date(now - msAgo).toISOString();
  const exceptionSubject = "Carrier says threshold only — customer booked room of choice";

  return {
    currentUserId: "tm-marc",
    team: [
      { id: "tm-marc", name: "Marc Tabuzo", title: "Customer Care · Midwest" },
      { id: "tm-kristine", name: "Kristine Catacte", title: "Customer Care · Midwest" },
      { id: "tm-tracy", name: "Tracy Morgan", title: "Operations · Midwest" },
      { id: "tm-darlyn", name: "Darlyn Lopez", title: "Customer Care · Central" },
    ],
    ticketGroups: [
      { id: "unassigned", name: "Unassigned" },
      { id: "assigned-to-me", name: "Assigned to Me" },
      { id: "exceptions", name: "Exceptions" },
      { id: "pod-requests", name: "POD Requests" },
      { id: "claims", name: "Claims" },
      { id: "billing", name: "Billing" },
      { id: "ap-intake", name: "AP Intake" },
      { id: "spot-quotes", name: "Spot quotes" },
    ],
    threads: [
      {
        id: "thread-48219",
        ticketId: "TKT-48219",
        subject: exceptionSubject,
        status: "waitingOnThirdParty",
        priority: "high",
        groupId: "exceptions",
        ownerId: "tm-marc",
        dueLabel: "SLA 4h 12m",
        order: ORDER,
        mailbox: CUSTOMER_CARE_MAILBOX,
        chains: CHAINS,
        emails: [
          inbound({
            id: "em-c1",
            inReplyTo: null,
            chain: "customer",
            subject: exceptionSubject,
            from: priya,
            to: [mailboxAddress],
            cc: [fornoOps],
            sentAt: at(DAY + 5 * HOUR),
            bodyText:
              "D. Washington says they were told room of choice with assembly when they booked. The driver is saying threshold only. Which is it?",
          }),
          outbound({
            id: "em-a1",
            inReplyTo: null,
            chain: "agent",
            subject: "Service level on DTSODR009352-2",
            to: [ana],
            cc: [],
            sentAt: at(DAY + 4 * HOUR + 50 * MINUTE),
            bodyText:
              "Can you confirm service level on DTSODR009352-2? Customer booked room of choice with assembly.",
            authorId: "tm-marc",
          }),
          outbound({
            id: "em-c2",
            inReplyTo: messageId("em-c1"),
            chain: "customer",
            subject: `Re: ${exceptionSubject}`,
            to: [priya],
            cc: [fornoOps],
            sentAt: at(DAY + 4 * HOUR + 45 * MINUTE),
            bodyText: "Checking with the agent now, I'll come back to you this evening.",
            authorId: "tm-marc",
          }),
          inbound({
            id: "em-a2",
            inReplyTo: messageId("em-a1"),
            chain: "agent",
            subject: "Re: Service level on DTSODR009352-2",
            from: ana,
            to: [mailboxAddress],
            cc: [],
            sentAt: at(DAY + 3 * HOUR),
            bodyText: "Our driver was dispatched threshold on this one — that's what the tender says.",
          }),
          outbound({
            id: "em-a3",
            inReplyTo: messageId("em-a2"),
            chain: "agent",
            subject: "Re: Service level on DTSODR009352-2",
            to: [ana],
            cc: [],
            sentAt: at(DAY + 2 * HOUR),
            bodyText:
              "Confirmed on our side, the order is tendered Threshold. Customer paid for room of choice with assembly — can you upgrade it?",
            authorId: "tm-marc",
          }),
          inbound({
            id: "em-c3",
            inReplyTo: messageId("em-c2"),
            chain: "customer",
            subject: `Re: ${exceptionSubject}`,
            from: priya,
            to: [mailboxAddress],
            cc: [fornoOps],
            sentAt: at(3 * HOUR + 18 * MINUTE),
            bodyText:
              "Any update? The consignee is taking the day off work for this. If it's threshold only we need to know now so we can reschedule rather than waste the trip.\n\nSeparately — can someone check DTSODR009897-2? Nothing on tracking since Friday.",
            quotedText:
              "On Sep 8, Dickerson Customer Care wrote:\n> Checking with the agent now, I'll come back to you this evening.",
          }),
          inbound({
            id: "em-a4",
            inReplyTo: messageId("em-a3"),
            chain: "agent",
            subject: "Re: Service level on DTSODR009352-2",
            from: ana,
            to: [mailboxAddress],
            cc: [],
            sentAt: at(18 * MINUTE),
            bodyText:
              "We can do room of choice with assembly for an additional $95.00. Crew is available tomorrow 13:00–17:00. Send a revised rate con and we'll lock it in.",
            quotedText:
              "On Sep 8, Dickerson Customer Care wrote:\n> Confirmed on our side, the order is tendered Threshold. Customer paid for room of choice with assembly — can you upgrade it?",
            attachments: [
              {
                id: "att-1",
                fileName: "Accessorial_schedule_SDT.pdf",
                contentType: "application/pdf",
                sizeBytes: 86016,
                filedToOrder: false,
              },
            ],
          }),
        ],
        notes: [
          {
            id: "note-1",
            authorId: "tm-marc",
            createdAt: at(4 * HOUR),
            body: "Service level on the order is Threshold, so S&D is right. The customer's expectation came from the original quote. @Kristine can you pull what Forno was quoted?",
            mentions: ["tm-kristine"],
          },
          {
            id: "note-2",
            authorId: "tm-kristine",
            createdAt: at(3 * HOUR + 40 * MINUTE),
            body: "@Marc Quote says Threshold. Forno's rep upsold room of choice verbally and never changed the booking. Worth offering the upgrade at our accessorial rate rather than eating it.",
            mentions: ["tm-marc"],
          },
        ],
        presence: [
          { memberId: "tm-kristine", activity: "replying", chain: "customer" },
          { memberId: "tm-tracy", activity: "viewing" },
        ],
        pendingDrafts: [
          {
            memberId: "tm-kristine",
            chain: "customer",
            recipientName: "Priya",
            startedAt: at(40 * 1000),
            bodyText:
              "Hi Priya — the carrier can upgrade to room of choice with assembly tomorrow between 13:00 and 17:00. Can the consignee be home for that window?",
          },
        ],
      },
      {
        id: "thread-48233",
        ticketId: "TKT-48233",
        subject: "POD request — delivery 09/06",
        status: "waitingOnPod",
        priority: "normal",
        groupId: "pod-requests",
        ownerId: null,
        dueLabel: "Auto-closes on POD",
        order: ORDER,
        mailbox: CUSTOMER_CARE_MAILBOX,
        chains: CHAINS,
        emails: [
          outbound({
            id: "em-p1",
            inReplyTo: null,
            chain: "agent",
            subject: "POD request — DTSODR009352-2",
            to: [ana],
            cc: [],
            sentAt: at(2 * DAY),
            bodyText: "Please send the signed POD for DTSODR009352-2, delivered 09/06.",
          }),
          outbound({
            id: "em-p2",
            inReplyTo: messageId("em-p1"),
            chain: "agent",
            subject: "Re: POD request — DTSODR009352-2",
            to: [ana],
            cc: [],
            sentAt: at(18 * MINUTE),
            bodyText:
              "Templated chase sent to S&D Transfer Logistics. Second reminder queued for 09/10.",
          }),
        ],
        notes: [],
        presence: [],
        pendingDrafts: [],
      },
      {
        id: "thread-48102",
        ticketId: "TKT-48102",
        subject: "Reschedule request — consignee unavailable 09/04",
        status: "resolved",
        priority: "normal",
        groupId: "exceptions",
        ownerId: "tm-darlyn",
        order: ORDER,
        mailbox: CUSTOMER_CARE_MAILBOX,
        chains: CHAINS,
        emails: [
          inbound({
            id: "em-r1",
            inReplyTo: null,
            chain: "customer",
            subject: "Reschedule request — consignee unavailable 09/04",
            from: priya,
            to: [mailboxAddress],
            cc: [],
            sentAt: at(6 * DAY),
            bodyText: "The consignee can't make 09/04. Can we move delivery to later in the week?",
          }),
          outbound({
            id: "em-r2",
            inReplyTo: null,
            chain: "agent",
            subject: "Reschedule DTSODR009352-2",
            to: [ana],
            cc: [],
            sentAt: at(6 * DAY - 2 * HOUR),
            bodyText: "Customer needs to move 09/04. What's your next availability?",
            authorId: "tm-darlyn",
          }),
          inbound({
            id: "em-r3",
            inReplyTo: messageId("em-r2"),
            chain: "agent",
            subject: "Re: Reschedule DTSODR009352-2",
            from: ana,
            to: [mailboxAddress],
            cc: [],
            sentAt: at(5 * DAY + 20 * HOUR),
            bodyText: "We can do 09/06, 12:00–16:00.",
          }),
          outbound({
            id: "em-r4",
            inReplyTo: messageId("em-r1"),
            chain: "customer",
            subject: "Re: Reschedule request — consignee unavailable 09/04",
            to: [priya],
            cc: [],
            sentAt: at(5 * DAY + 19 * HOUR),
            bodyText: "Rebooked to 09/06, 12:00–16:00. Appointment written back to the order.",
            authorId: "tm-darlyn",
          }),
          inbound({
            id: "em-r5",
            inReplyTo: messageId("em-r4"),
            chain: "customer",
            subject: "Re: Reschedule request — consignee unavailable 09/04",
            from: priya,
            to: [mailboxAddress],
            cc: [],
            sentAt: at(5 * DAY),
            bodyText: "Perfect, thank you.",
          }),
        ],
        notes: [],
        presence: [],
        pendingDrafts: [],
      },
    ],
  };
};

export const MOCK_CANNED_RESPONSES: CannedResponse[] = [
  {
    id: "canned-checking",
    title: "Checking with the carrier",
    body: "Thanks for flagging this — I'm checking with the carrier now and will come back to you today.",
  },
  {
    id: "canned-reschedule",
    title: "Offer a reschedule",
    body: "We can reschedule the delivery. Could you share two windows that suit the consignee this week?",
  },
  {
    id: "canned-pod",
    title: "POD chase",
    body: "Please send the signed proof of delivery for this order at your earliest convenience.",
  },
  {
    id: "canned-rate-con",
    title: "Revised rate confirmation",
    body: "Please find the revised rate confirmation attached. Reply to confirm and we'll update the order.",
  },
];
